import { SearchIcon } from 'icons/search-icon';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { FilterOption } from '../types';

export interface FilterOpenProps {
  searchPlaceholder: string;
  setQuery: (query: string) => void;
  selected: FilterOption[];
  setSelected: (selection: FilterOption[]) => void;
  options: FilterOption[];
  fetchNextPage?: () => void;
}

export const FilterOpen: FC<FilterOpenProps> = ({
  searchPlaceholder,
  selected,
  setSelected,
  setQuery,
  options,
  fetchNextPage,
}) => {
  const [search, setSearch] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const getCountForKey = (key: string): number => {
    const match = options.find(o => o.key === key);
    return match?.count ?? 0;
  };

  // Filter out already selected options
  const remaining = options.filter(
    option =>
      !selected.some(selectedOption => selectedOption.key === option.key),
  );

  // Debounce search query
  useEffect(() => {
    setQuery(search);
  }, [search, setQuery]);

  // Handle infinite scroll
  useEffect(() => {
    const container = scrollContainerRef.current;

    const handleScroll = () => {
      if (!container || !fetchNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage >= 0.8) {
        fetchNextPage();
      }
    };

    container?.addEventListener('scroll', handleScroll);

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [fetchNextPage]);

  return (
    <div
      ref={scrollContainerRef}
      className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[75] overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg"
    >
      {/* Search Input */}
      <div className="border-b border-gray-100 px-4 py-3">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 transform text-gray-400" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-md border border-gray-200 bg-gray-100 py-1.5 pl-10 pr-10 text-sm focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Selected Options (as checkboxes) */}
      {selected.map(option => (
        <div key={option.key} className="px-4 py-2 hover:bg-gray-50">
          <label className="flex min-w-0 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked
              onChange={() => {
                setSelected(
                  selected.filter(
                    selectedOption => selectedOption.key !== option.key,
                  ),
                );
              }}
              className="h-4 w-4 flex-shrink-0 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-blue-500"
            />
            <span
              className="min-w-0 truncate text-sm text-gray-900"
              title={option.label}
            >
              {option.label}
              <span className="ml-1 text-gray-500">
                ({getCountForKey(option.key)})
              </span>
            </span>
          </label>
        </div>
      ))}

      {/* Separator */}
      {selected.length > 0 && remaining.length > 0 && (
        <div className="my-1 border-t border-gray-100" />
      )}

      {/* Remaining Options */}
      {remaining.map(option => (
        <div key={option.key} className="px-4 py-2 hover:bg-gray-50">
          <label className="flex min-w-0 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={false}
              onChange={() => {
                setSelected([...selected, option]);
              }}
              className="h-4 w-4 flex-shrink-0 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-blue-500"
            />
            <span
              className="min-w-0 truncate text-sm text-gray-900"
              title={option.label}
            >
              {option.label}
              <span className="ml-1 text-gray-500">
                ({getCountForKey(option.key)})
              </span>
            </span>
          </label>
        </div>
      ))}

      {options.length === 0 && selected.length === 0 && (
        <div className="px-4 py-3 text-sm text-gray-500">
          <FormattedMessage
            defaultMessage="No options available"
            description="Message shown when no filter options are available"
          />
        </div>
      )}
    </div>
  );
};
